import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NavigateAction } from './azavista-ai-chatbot.types';

export type RouteResult = {
  commands: any[];
  queryParams: Record<string, string>;
};

type ParsedScope = {
  entities: { entity: string; id?: string }[];
  action?: string;
  subview?: string;
};

const ID_RE = /^(\{[a-z][a-z0-9_]*\}|[0-9a-f]{24}|\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
const ACTIONS = new Set(['list', 'create', 'edit', 'details', 'settings', 'dashboard', 'archive']);

export function parseView(view: string): ParsedScope {
  let scopeStr = view;
  let subview: string | undefined;
  const slash = view.indexOf('/');
  if (slash >= 0) {
    scopeStr = view.slice(0, slash);
    subview = view.slice(slash + 1);
  }
  const tokens = scopeStr.split('.');
  const entities: { entity: string; id?: string }[] = [];
  let action: string | undefined;
  for (let i = 0; i < tokens.length;) {
    const tok = tokens[i];
    if (ACTIONS.has(tok)) {
      if (i !== tokens.length - 1) throw new Error('action must be last');
      action = tok;
      i++;
      continue;
    }
    const next = tokens[i + 1];
    if (next && ID_RE.test(next)) {
      entities.push({ entity: tok, id: next });
      i += 2;
    } else {
      entities.push({ entity: tok });
      i += 1;
    }
  }
  return { entities, action, subview };
}

const isPlaceholder = (id?: string) => !!id && id.startsWith('{');
const scopeKey = (es: ParsedScope['entities']) =>
  es.map(e => (e.id !== undefined ? `${e.entity}.{id}` : e.entity)).join('.');

type Builder = (
  ids: Record<string, string>,
  action: string | undefined,
  subview: string | undefined,
  params: Record<string, any> | undefined,
) => RouteResult;

const ROUTES: Record<string, Builder> = {
  // events index — list / create (dialog on list page) / archive
  event: (_i, action) => {
    const seg = action === 'archive' ? 'archive' : 'list';
    return { commands: ['/events', seg], queryParams: {} };
  },
  series:           () => ({ commands: ['/events', 'series'],    queryParams: {} }),
  'series.{id}':    (i) => ({ commands: ['/events', 'series', i.series, 'details'], queryParams: {} }),
  'event-template': () => ({ commands: ['/events', 'templates'], queryParams: {} }),

  // per-event scope — the dashboard page hosts Dashboard/Details/Series/Teams/Settings tabs;
  // tab subviews aren't routes, so we just land on the page and let the user pick the tab
  'event.{id}': (i) => ({ commands: ['/event', 'dashboard'], queryParams: { eventId: i.event } }),

  // list / create, plus the global activity-settings & engagement-settings child routes (passed as subview)
  'event.{id}.activity': (i, action, subview) => ({
    commands: ['/event', 'activities', subview ?? (action === 'create' ? 'activity' : 'list')],
    queryParams: { eventId: i.event },
  }),
  // activity detail is a single mat-tab page — subview ignored, user picks the tab
  'event.{id}.activity.{id}': (i) => ({
    commands: ['/event', 'activities', 'activity'],
    queryParams: { eventId: i.event, activityId: i.activity },
  }),

  'event.{id}.participant': (i, action) => ({
    commands: ['/event', 'participants', action === 'create' ? 'event-participant-details' : 'list'],
    queryParams: { eventId: i.event },
  }),
  // participant detail is a single mat-tab page — subview ignored, user picks the tab
  'event.{id}.participant.{id}': (i) => ({
    commands: ['/event', 'participants', 'event-participant-details'],
    queryParams: { eventId: i.event, participantId: i.participant },
  }),

  // engagement-app is a single mat-tab page — subview ignored, user picks the tab
  'event.{id}.engagement-app':  (i)        => ({ commands: ['/event', 'engagement-app'],       queryParams: { eventId: i.event } }),
  // the next five are real Angular child routes → subview is a path segment (default child shown bare)
  'event.{id}.app':             (i, _a, s) => ({ commands: ['/event', 'event-app', s ?? 'settings'],            queryParams: { eventId: i.event } }),
  'event.{id}.meeting':         (i, _a, s) => ({ commands: ['/event', 'meetings-and-rooms', s ?? 'calendar'],   queryParams: { eventId: i.event } }),
  'event.{id}.ticket':          (i, _a, s) => ({ commands: ['/event', 'tickets-and-products', s ?? 'orders'],   queryParams: { eventId: i.event } }),
  'event.{id}.marketing':       (i, _a, s) => ({ commands: ['/event', 'event-marketing', s ?? 'link-generation'], queryParams: { eventId: i.event } }),
  'event.{id}.checkin':         (i, _a, s) => ({ commands: ['/event', 'onsite-check-in', s ?? 'badges'],        queryParams: { eventId: i.event } }),
  'event.{id}.website':         (i, _a, s) => ({ commands: ['/event', 'website', s ?? 'pages'],                       queryParams: { eventId: i.event } }),
  'event.{id}.content':         (i, _a, s) => ({ commands: ['/event', 'content', s ?? 'workflow-automations'],        queryParams: { eventId: i.event } }),
  'event.{id}.email-marketing': (i)        => ({ commands: ['/event', 'email-marketing'],      queryParams: { eventId: i.event } }),
  'event.{id}.event-field':        (i) => ({ commands: ['/event', 'fields-manager'], queryParams: { eventId: i.event } }),
  'event.{id}.participant-field':  (i) => ({ commands: ['/event', 'fields-manager'], queryParams: { eventId: i.event } }),
  'event.{id}.document':        (i)        => ({ commands: ['/event', 'documents'],            queryParams: { eventId: i.event } }),
  'event.{id}.report':          (i)        => ({ commands: ['/event', 'reports'],              queryParams: { eventId: i.event } }),
  'event.{id}.project':         (i)        => ({ commands: ['/event', 'project-management'],   queryParams: { eventId: i.event } }),

  // crm — list pages live at the empty path under their parent
  contact:           (_i, a) => ({ commands: a === 'create' ? ['/crm', 'contacts', 'contact-details'] : ['/crm', 'contacts'], queryParams: {} }),
  'contact.{id}':      (i)     => ({ commands: ['/crm', 'contacts', 'contact-details'], queryParams: { id: i.contact } }),
  organization:      (_i, a) => ({ commands: a === 'create' ? ['/crm', 'organizations', 'organization-details'] : ['/crm', 'organizations'], queryParams: {} }),
  'organization.{id}': (i)     => ({ commands: ['/crm', 'organizations', 'organization-details'], queryParams: { id: i.organization } }),
  'crm-dashboard':     ()      => ({ commands: ['/crm', 'dashboard'], queryParams: {} }),
  'profile-page':      ()      => ({ commands: ['/crm', 'profile-pages'], queryParams: {} }),
  'profile-page.{id}': (i)     => ({ commands: ['/crm', 'profile-pages', 'details', i['profile-page']], queryParams: {} }),

  // users — list pages live at the empty path under their parent
  user:            (_i, a) => ({ commands: a === 'create' ? ['/users', 'users', 'user-details'] : ['/users', 'users'], queryParams: {} }),
  'user.{id}':       (i)     => ({ commands: ['/users', 'users', 'user-details'], queryParams: { id: i.user } }),
  team:            (_i, a) => ({ commands: a === 'create' ? ['/users', 'teams', 'team-details'] : ['/users', 'teams'], queryParams: {} }),
  'team.{id}':       (i)     => ({ commands: ['/users', 'teams', 'team-details'], queryParams: { id: i.team } }),
  role:            (_i, a) => ({ commands: a === 'create' ? ['/users', 'roles', 'role-details'] : ['/users', 'roles'], queryParams: {} }),
  'role.{id}':       (i)     => ({ commands: ['/users', 'roles', 'role-details'], queryParams: { id: i.role } }),
  'api-client':      (_i, a) => ({ commands: a === 'create' ? ['/users', 'api-clients', 'api-client-details'] : ['/users', 'api-clients'], queryParams: {} }),
  'api-client.{id}': (i)     => ({ commands: ['/users', 'api-clients', 'api-client-details'], queryParams: { id: i['api-client'] } }),

  // global processes
  integration: (_i, a, _s, params) => {
    const commands = ['/global-processes', 'integrations'];
    if (a === 'create') {
      const qp: Record<string, string> = { create: '1' };
      if (params?.['type']) qp['type'] = String(params['type']);
      return { commands, queryParams: qp };
    }
    return { commands, queryParams: {} };
  },
  // integration detail is a single mat-tab page — subview ignored, user picks the tab
  'integration.{id}': (i) => ({
    commands: ['/global-processes', 'integrations', 'details'],
    queryParams: { id: i.integration },
  }),
  'email-template':       () => ({ commands: ['/global-processes', 'email-templates'], queryParams: {} }),
  'event-process':              ()  => ({ commands: ['/global-processes', 'global-processes', 'event-processes'], queryParams: {} }),
  'event-process.{id}':         (i) => ({ commands: ['/global-processes', 'global-processes', 'event-processes', 'process', i['event-process']], queryParams: {} }),
  'participant-process':        ()  => ({ commands: ['/global-processes', 'global-processes', 'participant-processes'], queryParams: {} }),
  'participant-process.{id}':   (i) => ({ commands: ['/global-processes', 'global-processes', 'participant-processes', 'process', i['participant-process']], queryParams: {} }),
  'contact-process':            ()  => ({ commands: ['/global-processes', 'global-processes', 'contact-processes'], queryParams: {} }),
  'contact-process.{id}':       (i) => ({ commands: ['/global-processes', 'global-processes', 'contact-processes', 'process', i['contact-process']], queryParams: {} }),
  'organization-process':       ()  => ({ commands: ['/global-processes', 'global-processes', 'organization-processes'], queryParams: {} }),
  'organization-process.{id}':  (i) => ({ commands: ['/global-processes', 'global-processes', 'organization-processes', 'process', i['organization-process']], queryParams: {} }),

  // top-level singletons
  dashboard:               () => ({ commands: ['/dashboard'], queryParams: {} }),
  'website-builder':         () => ({ commands: ['/website-builder'], queryParams: {} }),
  document:                () => ({ commands: ['/documents'], queryParams: {} }),
  import:                  () => ({ commands: ['/import'], queryParams: {} }),
  job:                     () => ({ commands: ['/my-jobs'], queryParams: {} }),
  account:                 () => ({ commands: ['/my-account'], queryParams: {} }),
  'org-setting':             () => ({ commands: ['/organization', 'settings'], queryParams: {} }),
  'system-email':            () => ({ commands: ['/organization', 'system-email'], queryParams: {} }),
  report:                  () => ({ commands: ['/reports'], queryParams: {} }),
  'report.{id}':             (i) => ({ commands: ['/reports', 'details'], queryParams: { reportId: i.report } }),
  'contact-field':         () => ({ commands: ['/fields-manager', 'list'], queryParams: {} }),
  'organization-field':    () => ({ commands: ['/fields-manager', 'list'], queryParams: {} }),
  'event-field':           () => ({ commands: ['/fields-manager', 'list'], queryParams: {} }),
  ticket:                  () => ({ commands: ['/tickets-and-products', 'products'], queryParams: {} }),
  'ticket.{id}':             (i) => ({ commands: ['/tickets-and-products', 'products', 'details'], queryParams: { id: i.ticket } }),
  'product-group':           () => ({ commands: ['/tickets-and-products', 'product-groups'], queryParams: {} }),
  tax:                     () => ({ commands: ['/tickets-and-products', 'taxes'], queryParams: {} }),
  'cancellation-term':       () => ({ commands: ['/tickets-and-products', 'cancellation-terms'], queryParams: {} }),
  order:                   () => ({ commands: ['/tickets-and-products', 'orders'], queryParams: {} }),
  discount:                () => ({ commands: ['/tickets-and-products', 'discounts'], queryParams: {} }),
  theme:                   () => ({ commands: ['/theme'], queryParams: {} }),
  'theme.{id}':              (i) => ({ commands: ['/theme', 'details', i.theme], queryParams: {} }),
  'header-footer':           () => ({ commands: ['/theme', 'headers-and-footers'], queryParams: {} }),
  project:                 () => ({ commands: ['/project-management', 'projects'], queryParams: {} }),
  'project-template':        () => ({ commands: ['/project-management', 'project-templates'], queryParams: {} }),
  'project-category':        () => ({ commands: ['/project-management', 'categories'], queryParams: {} }),

  // standalone event-app manager (distinct from the per-event event.{id}.app config)
  app:                     () => ({ commands: ['/event-app', 'list'], queryParams: {} }),
  'app.{id}':                (i) => ({ commands: ['/event-app', i.app, 'details'], queryParams: {} }),

  // rooms management — /rooms-management is the locations list; rooms/amenities/floor-plans live under a location
  location:                      ()  => ({ commands: ['/rooms-management'], queryParams: {} }),
  'location.{id}':               (i) => ({ commands: ['/rooms-management', 'details'], queryParams: { id: i.location } }),
  'location.{id}.room':          (i) => ({ commands: ['/rooms-management', 'details', 'room'], queryParams: { id: i.location } }),
  'location.{id}.amenity':       (i) => ({ commands: ['/rooms-management', 'details', 'amenity'], queryParams: { id: i.location } }),
  'location.{id}.floor-plan':    (i) => ({ commands: ['/rooms-management', 'details', 'floor-plan'], queryParams: { id: i.location } }),

  // /contacts-email-marketing has email-campaigns (default), email-campaign-types, utm-parameters as child routes
  'contact-email-marketing': (_i, _a, s) => ({ commands: ['/contacts-email-marketing', s ?? 'email-campaigns'], queryParams: {} }),
};

export function viewToRoute(action: NavigateAction): RouteResult | null {
  const parsed = parseView(action.view);
  for (const e of parsed.entities) {
    if (isPlaceholder(e.id)) return null;
  }
  const builder = ROUTES[scopeKey(parsed.entities)];
  if (!builder) return null;
  const ids: Record<string, string> = {};
  for (const e of parsed.entities) if (e.id) ids[e.entity] = e.id;
  return builder(ids, parsed.action, parsed.subview, action.params);
}

// ─── route → canonical view ────────────────────────────────────────────────

type RoutePattern = {
  match: RegExp;
  build: (m: RegExpMatchArray, qp: URLSearchParams) => string | null;
};

// Maps `/event/<area>[/<child>]` to a canonical view. For the five child-route
// areas the child segment becomes a subview; for the rest it's ignored.
const EVENT_AREA_VIEWS: Record<string, (eid: string, child?: string) => string> = {
  dashboard: eid => `event.${eid}`,
  'engagement-app': eid => `event.${eid}.engagement-app`,
  'event-app': (eid, c) => `event.${eid}.app${c ? `/${c}` : ''}`,
  'meetings-and-rooms': (eid, c) => `event.${eid}.meeting${c ? `/${c}` : ''}`,
  'tickets-and-products': (eid, c) => `event.${eid}.ticket${c ? `/${c}` : ''}`,
  'event-marketing': (eid, c) => `event.${eid}.marketing${c ? `/${c}` : ''}`,
  'onsite-check-in': (eid, c) => `event.${eid}.checkin${c ? `/${c}` : ''}`,
  website: (eid, c) => `event.${eid}.website${c ? `/${c}` : ''}`,
  content: (eid, c) => `event.${eid}.content${c ? `/${c}` : ''}`,
  'email-marketing': eid => `event.${eid}.email-marketing`,
  documents: eid => `event.${eid}.document`,
  reports: eid => `event.${eid}.report`,
  'project-management': eid => `event.${eid}.project`,
};

const ROUTE_TO_VIEW: RoutePattern[] = [
  // event-scoped pages
  { match: /^\/event\/activities\/list$/, build: (_m, qp) => {
      const eid = qp.get('eventId');
      return eid ? `event.${eid}.activity.list` : null;
    } },
  { match: /^\/event\/activities\/activity$/, build: (_m, qp) => {
      const eid = qp.get('eventId');
      if (!eid) return null;
      const aid = qp.get('activityId');
      return aid ? `event.${eid}.activity.${aid}` : `event.${eid}.activity.create`;
    } },
  { match: /^\/event\/participants\/list$/, build: (_m, qp) => {
      const eid = qp.get('eventId');
      return eid ? `event.${eid}.participant.list` : null;
    } },
  { match: /^\/event\/participants\/event-participant-details$/, build: (_m, qp) => {
      const eid = qp.get('eventId');
      if (!eid) return null;
      const pid = qp.get('participantId');
      return pid ? `event.${eid}.participant.${pid}` : `event.${eid}.participant.create`;
    } },
  { match: /^\/event\/activities\/(activity-settings|engagement-settings)$/, build: (m, qp) => {
      const eid = qp.get('eventId');
      return eid ? `event.${eid}.activity/${m[1]}` : null;
    } },
  { match: /^\/event\/([a-z-]+)(?:\/([a-z-]+))?(?:\/.*)?$/, build: (m, qp) => {
      const eid = qp.get('eventId');
      const builder = EVENT_AREA_VIEWS[m[1]];
      return eid && builder ? builder(eid, m[2]) : null;
    } },

  // events index ( /events/series has its own list + {id}/details child routes )
  { match: /^\/events\/series\/([^/]+)\/details\b/, build: m => `series.${m[1]}` },
  { match: /^\/events\/series\b/, build: () => 'series.list' },
  { match: /^\/events\/(list|templates|archive)$/, build: m => {
      if (m[1] === 'list')      return 'event.list';
      if (m[1] === 'archive')   return 'event.archive';
      if (m[1] === 'templates') return 'event-template.list';
      return null;
    } },

  // crm — list pages live at the empty path; detail pages read ?id=
  { match: /^\/crm\/contacts$/, build: () => 'contact.list' },
  { match: /^\/crm\/contacts\/contact-details$/, build: (_m, qp) => {
      const id = qp.get('id');
      return id ? `contact.${id}` : 'contact.create';
    } },
  { match: /^\/crm\/organizations$/, build: () => 'organization.list' },
  { match: /^\/crm\/organizations\/organization-details$/, build: (_m, qp) => {
      const id = qp.get('id');
      return id ? `organization.${id}` : 'organization.create';
    } },
  { match: /^\/crm\/dashboard\b/, build: () => 'crm-dashboard' },
  { match: /^\/crm\/profile-pages\/details\/([^/]+)/, build: m => `profile-page.${m[1]}` },
  { match: /^\/crm\/profile-pages\b/, build: () => 'profile-page' },

  // users — list pages live at the empty path; detail pages read ?id=
  { match: /^\/users\/users$/, build: () => 'user.list' },
  { match: /^\/users\/users\/user-details$/, build: (_m, qp) => {
      const id = qp.get('id');
      return id ? `user.${id}` : 'user.create';
    } },
  { match: /^\/users\/teams$/, build: () => 'team.list' },
  { match: /^\/users\/teams\/team-details$/, build: (_m, qp) => {
      const id = qp.get('id');
      return id ? `team.${id}` : 'team.create';
    } },
  { match: /^\/users\/roles$/, build: () => 'role.list' },
  { match: /^\/users\/roles\/role-details$/, build: (_m, qp) => {
      const id = qp.get('id');
      return id ? `role.${id}` : 'role.create';
    } },
  { match: /^\/users\/api-clients$/, build: () => 'api-client.list' },
  { match: /^\/users\/api-clients\/api-client-details$/, build: (_m, qp) => {
      const id = qp.get('id');
      return id ? `api-client.${id}` : 'api-client.create';
    } },

  // global processes
  { match: /^\/global-processes\/integrations$/, build: () => 'integration.list' },
  { match: /^\/global-processes\/integrations\/details$/, build: (_m, qp) => {
      const id = qp.get('id');
      return id ? `integration.${id}` : null;
    } },
  { match: /^\/global-processes\/email-templates\b/, build: () => 'email-template' },
  { match: /^\/global-processes\/global-processes\/(event|participant|contact|organization)-processes\/process\/([^/]+)/, build: m => `${m[1]}-process.${m[2]}` },
  { match: /^\/global-processes\/global-processes\/(event|participant|contact|organization)-processes\b/, build: m => `${m[1]}-process` },

  // singletons
  { match: /^\/dashboard\b/,               build: () => 'dashboard' },
  { match: /^\/website-builder\b/,         build: () => 'website-builder' },
  { match: /^\/documents\b/,               build: () => 'document' },
  { match: /^\/import\b/,                  build: () => 'import' },
  { match: /^\/my-jobs\b/,                 build: () => 'job' },
  { match: /^\/my-account\b/,              build: () => 'account' },
  { match: /^\/organization\/settings\b/,  build: () => 'org-setting' },
  { match: /^\/organization\/system-email\b/, build: () => 'system-email' },
  { match: /^\/reports\/details\b/,        build: (_m, qp) => {
      const id = qp.get('reportId');
      return id ? `report.${id}` : 'report';
    } },
  { match: /^\/reports\b/,                 build: () => 'report' },
  { match: /^\/tickets-and-products\/products\/details\b/, build: (_m, qp) => {
      const id = qp.get('id');
      return id ? `ticket.${id}` : 'ticket';
    } },
  { match: /^\/tickets-and-products\/products\b/,            build: () => 'ticket' },
  { match: /^\/tickets-and-products\/product-groups\b/,      build: () => 'product-group' },
  { match: /^\/tickets-and-products\/taxes\b/,               build: () => 'tax' },
  { match: /^\/tickets-and-products\/cancellation-terms\b/,  build: () => 'cancellation-term' },
  { match: /^\/tickets-and-products\/orders\b/,              build: () => 'order' },
  { match: /^\/tickets-and-products\/discounts\b/,           build: () => 'discount' },
  { match: /^\/theme\/headers-and-footers\b/, build: () => 'header-footer' },
  { match: /^\/theme\/details\/([^/]+)/,   build: m => `theme.${m[1]}` },
  { match: /^\/theme\b/,                   build: () => 'theme' },
  { match: /^\/project-management\/projects\b/,          build: () => 'project' },
  { match: /^\/project-management\/project-templates\b/, build: () => 'project-template' },
  { match: /^\/project-management\/categories\b/,        build: () => 'project-category' },
  { match: /^\/event-app\/([^/]+)\/details\b/, build: m => `app.${m[1]}` },
  { match: /^\/event-app\b/,               build: () => 'app.list' },

  // rooms management — /rooms-management is locations list; /details + ?id= is one location
  { match: /^\/rooms-management\/details\/(room|amenity|floor-plan)\b/, build: (m, qp) => {
      const id = qp.get('id');
      return id ? `location.${id}.${m[1]}.list` : null;
    } },
  // TODO: add location's room/amenity/floor-plan
  { match: /^\/rooms-management\/details\b/, build: (_m, qp) => {
      const id = qp.get('id');
      return id ? `location.${id}` : null;
    } },
  { match: /^\/rooms-management\b/,        build: () => 'location.list' },

  { match: /^\/contacts-email-marketing(?:\/([a-z-]+))?/, build: m =>
      `contact-email-marketing${m[1] ? `/${m[1]}` : ''}` },
];

/** Map an Angular URL (e.g. `/event/activities/list?eventId=abc`) to a canonical view, or null if unknown. */
export function routeToView(url: string): string | null {
  const withoutFragment = url.split('#', 2)[0];
  const [path, queryStr = ''] = withoutFragment.split('?', 2);
  const qp = new URLSearchParams(queryStr);
  for (const rule of ROUTE_TO_VIEW) {
    const m = path.match(rule.match);
    if (m) return rule.build(m, qp);
  }
  return null;
}

@Injectable({ providedIn: 'root' })
export class ViewNavigatorService {
  private router = inject(Router);

  async navigate(action: NavigateAction): Promise<boolean> {
    const target = viewToRoute(action);
    if (!target) return false;
    return this.router.navigate(target.commands, { queryParams: target.queryParams });
  }
}
